import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/config/supabase';
import { STRIPE_CONFIG } from '@/config/stripe';

type Plan = 'free' | 'plus' | 'pro';

interface SubscriptionContextType {
  plan: Plan;
  isPlusOrHigher: boolean;
  isPro: boolean;
  loading: boolean;
  subscribe: (tier: 'plus' | 'pro') => Promise<void>;
  manageSubscription: () => void;
  stripeCustomerId: string | null;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [plan, setPlan] = useState<Plan>('free');
  const [stripeCustomerId, setStripeCustomerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Load subscription status from Supabase
  useEffect(() => {
    if (!user?.id) {
      setPlan('free');
      setLoading(false);
      return;
    }

    const loadSubscription = async () => {
      try {
        const { data, error } = await supabase
          .from('subscriptions')
          .select('plan, status, stripe_customer_id, current_period_end')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error || !data) {
          setPlan('free');
          setLoading(false);
          return;
        }

        // Check if subscription is still active
        const isActive = data.status === 'active' &&
          (!data.current_period_end || new Date(data.current_period_end) > new Date());

        setPlan(isActive ? (data.plan as Plan) : 'free');
        setStripeCustomerId(data.stripe_customer_id);
      } catch {
        setPlan('free');
      } finally {
        setLoading(false);
      }
    };

    loadSubscription();
  }, [user?.id]);

  // Redirect to Stripe Payment Link
  const subscribe = useCallback(async (tier: 'plus' | 'pro') => {
    const paymentLinkUrl = tier === 'plus'
      ? import.meta.env.VITE_STRIPE_PLUS_PAYMENT_LINK
      : import.meta.env.VITE_STRIPE_PRO_PAYMENT_LINK;

    if (!paymentLinkUrl) {
      console.warn('Stripe payment link not configured for tier:', tier);
      return;
    }

    // Append user email and success/cancel URLs as params
    const email = user?.primaryEmailAddress?.emailAddress || '';
    const successUrl = `${window.location.origin}/subscription/success?plan=${tier}`;
    const url = new URL(paymentLinkUrl);
    if (email) url.searchParams.set('prefilled_email', email);
    url.searchParams.set('client_reference_id', user?.id || '');

    window.location.href = url.toString();
  }, [user]);

  // Open Stripe Customer Portal for managing subscription / billing history
  const manageSubscription = useCallback(() => {
    if (STRIPE_CONFIG.customerPortalUrl) {
      window.open(STRIPE_CONFIG.customerPortalUrl, '_blank');
    }
  }, []);

  return (
    <SubscriptionContext.Provider value={{
      plan,
      isPlusOrHigher: plan === 'plus' || plan === 'pro',
      isPro: plan === 'pro',
      loading,
      subscribe,
      manageSubscription,
      stripeCustomerId,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
